/**
 * Cart Persistence Web Component
 *
 * Manages the save/restore lifecycle:
 * - Variant A: restore via <shopify-account> sign-in
 * - Variant B: save via email capture → POST /a/accounts/carts
 * - Variant D/E: silent sync via PATCH /a/accounts/carts on cart:update
 * - Auto-restore on cart surfaces for logged-in users via GET /a/accounts/carts/mine
 *
 * Active for all users.
 */

const PERSIST_EMAIL_KEY = 'cart_persist_email';
const DISMISS_KEY = 'cart_save_prompt_dismissed';
const RESTORE_ATTEMPTED_KEY = 'cart_restore_attempted';
const RESTORE_SUCCEEDED_KEY = 'cart_persist_restore_succeeded';
const PERSIST_EMAIL_PENDING_KEY = 'cart_persist_email_pending';
const DEVICE_ID_KEY = 'cart_persist_device_id';
const API_BASE = '/a/accounts/carts';
const DEBOUNCE_MS = 750;

class CartPersistence extends HTMLElement {
  #pollInterval = null;
  #initialized = false;
  #visibilityObserver = null;

  connectedCallback() {
    this.#init();
  }

  #init() {
    if (this.#initialized) return;
    if (!this.isConnected) return;
    this.#initialized = true;
    cartSyncManager.enabled = true;
    this.#initDeviceId();
    this.#bindEvents();
    this.#resolveInitialState();
  }

  #show() {
    this.style.display = '';
  }

  disconnectedCallback() {
    if (this.#pollInterval) clearInterval(this.#pollInterval);
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
  }

  // ── Device ID ──────────────────────────────────────────────────────────

  #initDeviceId() {
    if (!localStorage.getItem(DEVICE_ID_KEY)) {
      localStorage.setItem(DEVICE_ID_KEY, crypto.randomUUID());
    }
  }

  get #deviceId() {
    return localStorage.getItem(DEVICE_ID_KEY);
  }

  // ── State ──────────────────────────────────────────────────────────────

  get #isLoggedIn() {
    return this.dataset.loggedIn === 'true';
  }

  get #hasItems() {
    return this.dataset.hasItems === 'true';
  }

  get #persistEmail() {
    return localStorage.getItem(PERSIST_EMAIL_KEY);
  }

  get #isDismissed() {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  }

  // "drawer-footer" context = render the compact bar as the primary CTA,
  // never the modal-style Variant B card (wireframe items 1 / R2).
  get #isFooterMode() {
    return this.dataset.context === 'drawer-footer';
  }

  // ── Event binding ─────────────────────────────────────────────────────

  #bindEvents() {
    // Save form submission
    const form = this.querySelector('[data-action="save"]');
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.#handleSaveSubmit(form);
      });
    }

    // Dismiss button
    const dismissBtn = this.querySelector('[data-action="dismiss"]');
    if (dismissBtn) {
      dismissBtn.addEventListener('click', () => this.#handleDismiss());
    }

    // Compact bar — expand back to Variant B
    const compactBtn = this.querySelector('[data-action="show-save"]');
    if (compactBtn) {
      compactBtn.addEventListener('click', () => this.#showVariant('b'));
    }

    // Variant A restore trigger — clicks the hidden <shopify-account> button
    const restoreTrigger = this.querySelector('[data-action="open-account"]');
    const accountComp = this.querySelector('shopify-account');
    if (restoreTrigger && accountComp) {
      restoreTrigger.addEventListener('click', () => {
        const inner = accountComp.shadowRoot?.querySelector('button');
        if (inner) {
          inner.click();
        }
      });
    }

    // shopify-account open/close events
    if (accountComp) {
      accountComp.addEventListener('open', () => {
        this.#track('cart_restore_auth_opened');
      });
      accountComp.addEventListener('close', () => {
        this.#track('cart_restore_auth_closed');
        this.#pollForSignIn();
      });
    }

  }

  // ── Initial state resolution ──────────────────────────────────────────

  #resolveInitialState() {
    const email = this.#persistEmail;

    if (email) {
      // Email known (from prior save on this device) — show saved indicator
      this.#showSavedIndicator(email);
      this.#show();
      if (this.#isLoggedIn) {
        this.#attemptRestore();
      }
      return;
    }

    // Check for a pending email from newsletter signup (written pre-reload).
    // silentCapture POSTs the cart; on success sets PERSIST_EMAIL_KEY.
    // On failure, pending key stays so next page load retries.
    const pendingEmail = localStorage.getItem(PERSIST_EMAIL_PENDING_KEY);
    if (pendingEmail && this.#hasItems && !this.#isLoggedIn) {
      const self = this;
      silentCapture(pendingEmail, 'newsletter').then((ok) => {
        if (ok) {
          localStorage.removeItem(PERSIST_EMAIL_PENDING_KEY);
          self.#showSavedIndicator(pendingEmail);
          self.#show();
        }
      });
      return;
    }

    // Logged-in user (no localStorage email).
    // Attempt restore first; if nothing to restore, start persisting the
    // current cart so future sessions on other devices can recover it.
    if (this.#isLoggedIn) {
      // Clean up any pending newsletter email — logged-in identity takes precedence
      localStorage.removeItem(PERSIST_EMAIL_PENDING_KEY);
      if (sessionStorage.getItem(RESTORE_SUCCEEDED_KEY)) {
        this.#showSavedIndicator('');
        this.#show();
        return;
      }
      this.#attemptRestore();
      return;
    }

    // Footer mode (drawer redesign): always surface Variant F when cart
    // has items. Clicking the compact bar expands Variant B inline.
    if (this.#isFooterMode) {
      if (this.#hasItems) {
        this.#showVariant('f');
        this.#show();
        this.#trackOnceVisible(() => {
          this.#track('cart_save_prompt_impression', {
            variant: 'f',
            context: 'drawer-footer',
          });
        });
      } else {
        // Empty cart in footer mode — hide altogether.
        this.style.display = 'none';
      }
      return;
    }

    // Email not known, not logged in — show appropriate prompt
    if (this.#isDismissed && this.#hasItems) {
      this.#showVariant('f');
      this.#show();
      this.#trackOnceVisible(() => {
        this.#track('cart_save_prompt_impression', { variant: 'f' });
      });
    } else if (this.#hasItems) {
      // Cart has items, first-time guest: delay the email prompt (Variant B)
      // so the checkout CTA is visible and unobstructed when the drawer opens.
      // Delay is measured from when the component becomes visible (drawer open),
      // not from element mount, since <cart-persistence> mounts at page load
      // but only matters when the user actually sees it.
      const variantB = this.querySelector('[data-variant="b"]');
      if (variantB) variantB.style.display = 'none';
      this.#show();

      this.#trackOnceVisible(() => {
        setTimeout(() => {
          if (!this.isConnected) return;
          if (variantB) variantB.style.display = '';
          this.#track('cart_save_prompt_impression', { variant: 'b' });
        }, 3000);
      });
    } else {
      // Empty cart, first-time guest: no prompt to show. Keep Variant B hidden
      // so it doesn't surface on an empty cart, but still track the impression
      // once the surface is visible.
      const variantB = this.querySelector('[data-variant="b"]');
      if (variantB) variantB.style.display = 'none';
      this.#show();

      this.#trackOnceVisible(() => {
        this.#track('cart_restore_surface_seen');
      });
    }
  }

  // ── Save flow (Variant B) ─────────────────────────────────────────────

  async #handleSaveSubmit(form) {
    const input = form.querySelector('input[name="email"]');
    const email = input?.value?.trim().toLowerCase();
    if (!email) return;

    const submitBtn = form.querySelector('button[type="submit"]');
    const originalText = submitBtn?.textContent;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = '...';
    }

    try {
      const cart = await this.#fetchShopifyCart();
      const items = this.#serializeItems(cart.items);

      const resp = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          email,
          items,
          source: 'save_prompt',
          deviceId: this.#deviceId,
        }),
      });

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        if (resp.status === 429) {
          console.warn('[cart-persist] Rate limited, retry later');
        } else {
          console.error('[cart-persist] Save failed:', data.error);
        }
        return;
      }

      localStorage.setItem(PERSIST_EMAIL_KEY, email);
      this.#showSavedIndicator(email);
      this.#track('cart_save_submit', { email_masked: this.#maskEmail(email) });
    } catch (err) {
      console.error('[cart-persist] Save error:', err);
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalText;
      }
    }
  }

  // Sync is handled at module scope — see cartSyncManager below.
  // This avoids the debounce timer being killed by section morphing
  // (which destroys and recreates the <cart-persistence> element).

  // ── Restore flow (logged-in, GET /carts/mine) ─────────────────────────

  async #attemptRestore() {
    if (sessionStorage.getItem(RESTORE_ATTEMPTED_KEY)) return;
    cartSyncManager.restoring = true;

    try {
      const resp = await fetch(`${API_BASE}/mine`, {
        credentials: 'same-origin',
      });

      this.#track('cart_restore_fetch_result', { status: resp.status });

      // Only mark as attempted on definitive results, not transient failures
      if (resp.status === 404 || resp.ok) {
        sessionStorage.setItem(RESTORE_ATTEMPTED_KEY, '1');
      }

      if (resp.status === 404) {
        // No stored cart — hide only for fresh-device users (no prior save).
        // Returning users with localStorage email already have Variant D visible.
        if (!this.#persistEmail) this.style.display = 'none';
        return;
      }
      if (!resp.ok) return; // Transient error — will retry next session/surface

      const { items: remoteItems } = await resp.json();
      if (!remoteItems?.length) {
        if (!this.#persistEmail) this.style.display = 'none';
        return;
      }

      // Read local Shopify cart
      const localCart = await this.#fetchShopifyCart();
      const localItems = localCart.items || [];

      // Merge: add missing remote lines, max(qty) for matching
      const { successCount, skippedCount, unavailableCount } =
        await this.#mergeAndApply(localItems, remoteItems);

      if (successCount > 0) {
        // Mark success so re-initialized elements (after section morph) can
        // show the indicator without re-fetching.
        sessionStorage.setItem(RESTORE_SUCCEEDED_KEY, '1');

        // Show the saved indicator for fresh-device users who didn't have one yet
        if (!this.#persistEmail) {
          this.#showSavedIndicator('');
          this.#show();
        }

        document.dispatchEvent(
          new CustomEvent('cart:restored', {
            detail: { count: successCount, unavailable: unavailableCount },
          })
        );
        this.#track('cart_restore_items_added', { count: successCount });
      }
      if (skippedCount > 0) {
        this.#track('cart_restore_items_skipped', { count: skippedCount });
      }
      if (unavailableCount > 0) {
        this.#track('cart_restore_items_unavailable', { count: unavailableCount });
      }
    } catch (err) {
      console.error('[cart-persist] Restore error:', err);
    } finally {
      cartSyncManager.restoring = false;
      // Fire deferred initial sync now that restore is complete.
      // Ensures the current cart state (post-merge) is persisted to cart-api.
      if (this.#hasItems) {
        cartSyncManager.handleCartUpdate();
      }
    }
  }

  // ── Merge & apply ─────────────────────────────────────────────────────

  async #mergeAndApply(localItems, remoteItems) {
    const toAdd = [];
    const toUpdate = [];
    let skippedCount = 0;
    let unavailableCount = 0;

    // Build local index by dedupe key
    const localByKey = new Map();
    for (const item of localItems) {
      localByKey.set(this.#dedupeKey(item), item);
    }

    // Collect local gallery wall bundle IDs
    const localBundleIds = new Set();
    for (const item of localItems) {
      const gwId = item.properties?._gallery_wall_id;
      if (gwId) localBundleIds.add(gwId);
    }

    for (const remote of remoteItems) {
      const key = this.#dedupeKey(remote);
      const local = localByKey.get(key);

      // Gallery wall bundle conflict: local wins
      const gwId = remote.properties?._gallery_wall_id;
      if (gwId && localBundleIds.has(gwId) && !local) {
        skippedCount++;
        continue;
      }

      if (local) {
        // Matching line exists — max(qty)
        if (remote.quantity > local.quantity) {
          toUpdate.push({ key: local.key, quantity: remote.quantity });
        } else {
          skippedCount++;
        }
      } else {
        // Missing locally — add
        toAdd.push({
          id: remote.variantId,
          quantity: remote.quantity,
          properties: remote.properties || {},
        });
      }
    }

    let successCount = 0;

    // Apply updates (qty bumps)
    for (const { key, quantity } of toUpdate) {
      try {
        const resp = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: key, quantity }),
        });
        if (resp.ok) {
          successCount++;
        } else {
          unavailableCount++;
          console.warn('[cart-persist] Qty update failed:', key);
        }
      } catch (err) {
        unavailableCount++;
        console.error('[cart-persist] Qty update failed:', err);
      }
    }

    // Batch-add all items in a single request. If the batch fails (one stale
    // variant rejects the whole batch), fall back to one-by-one to isolate
    // which items are unavailable.
    if (toAdd.length > 0) {
      try {
        const resp = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ items: toAdd }),
        });
        if (resp.ok) {
          successCount += toAdd.length;
        } else {
          // Batch rejected — fall back to individual adds
          for (const item of toAdd) {
            try {
              const r = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'same-origin',
                body: JSON.stringify({ items: [item] }),
              });
              if (r.ok) {
                successCount++;
              } else {
                unavailableCount++;
                console.warn('[cart-persist] Item unavailable:', item.id);
              }
            } catch {
              unavailableCount++;
            }
          }
        }
      } catch (err) {
        console.error('[cart-persist] Batch add failed:', err);
        unavailableCount += toAdd.length;
      }
    }

    // Re-render cart sections if anything changed.
    // Set restoring flag to suppress the sync listener from echoing this back.
    // The flag is cleared by #attemptRestore's finally block (which also fires
    // the deferred initial sync), so no rAF cleanup is needed here.
    if (successCount > 0) {
      cartSyncManager.restoring = true;
      const freshCart = await this.#fetchShopifyCart();
      document.dispatchEvent(
        new CustomEvent('cart:update', { detail: { data: freshCart } })
      );
    }

    return { successCount, skippedCount, unavailableCount };
  }

  // ── Dedupe key (mirrors server-side cart-merge.server.js) ──────────────

  #dedupeKey(item) {
    const props = item.properties || {};
    const sorted = Object.keys(props)
      .filter((k) => k !== 'key')
      .sort()
      .reduce((acc, k) => { acc[k] = props[k]; return acc; }, {});
    // Simple string hash — doesn't need to match server's sha256, just be consistent client-side
    const str = JSON.stringify(sorted);
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
    }
    const vid = item.variant_id || item.variantId || item.id;
    return `${vid}:${hash}`;
  }

  // ── Serialize Shopify cart items for cart-api ──────────────────────────

  #serializeItems(items) {
    return (items || []).map((item) => ({
      variantId: String(item.variant_id || item.id),
      quantity: item.quantity,
      properties: item.properties || {},
      // Display fields for Klaviyo abandoned-cart emails
      title: item.title || '',
      price: item.price ?? null,
      image: item.image || item.featured_image?.url || '',
      url: item.url || '',
      productType: item.product_type || '',
    }));
  }

  // ── Poll for sign-in after shopify-account close ──────────────────────

  async #pollForSignIn() {
    // After the shopify-account dialog closes, the user may have signed in.
    // We can't detect this client-side without a page reload (Liquid is server-rendered).
    // Poll by re-fetching /cart.js — if customer is now logged in, the Shopify session
    // will differ. We detect this by re-fetching our own section which includes
    // data-logged-in on the cart-persistence element.
    let attempts = 0;
    const maxAttempts = 5;
    this.#pollInterval = setInterval(async () => {
      attempts++;
      if (attempts > maxAttempts) {
        clearInterval(this.#pollInterval);
        this.#pollInterval = null;
        return;
      }
      try {
        const sectionId = this.closest('[data-section-id]')?.dataset.sectionId;
        const url = sectionId
          ? `/?section_id=${sectionId}`
          : '/?section_id=main-cart';
        const resp = await fetch(url, { credentials: 'same-origin' });
        const html = await resp.text();
        if (html.includes('data-logged-in="true"')) {
          clearInterval(this.#pollInterval);
          this.#pollInterval = null;
          window.location.reload();
        }
      } catch {
        // ignore
      }
    }, 1500);
  }

  // ── UI variant switching ──────────────────────────────────────────────

  #showVariant(variant) {
    const all = this.querySelectorAll('[data-variant]');
    all.forEach((el) => {
      el.style.display = el.dataset.variant === variant ? '' : 'none';
    });
  }

  #showSavedIndicator(email) {
    const textEl = this.querySelector('[data-persist-email]');
    if (textEl) {
      textEl.textContent = email
        ? `Cart saved to ${email}`
        : 'Cart saved to your account';
    }
    this.#showVariant('d');
  }

  #handleDismiss() {
    sessionStorage.setItem(DISMISS_KEY, '1');
    this.#showVariant('f');
    this.#track('cart_save_prompt_dismiss');
  }

  // ── Helpers ───────────────────────────────────────────────────────────

  async #fetchShopifyCart() {
    const resp = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!resp.ok) throw new Error(`/cart.js ${resp.status}`);
    return resp.json();
  }

  #maskEmail(email) {
    if (!email) return '';
    const [local, domain] = email.split('@');
    if (!domain) return email;
    const masked = local.charAt(0) + '••';
    return `${masked}@${domain}`;
  }

  // Public method — called by silentCapture to update visible indicator
  updateIndicator(email) {
    this.#showSavedIndicator(email);
    this.#show();
  }

  #trackOnceVisible(callback) {
    if (!this.isConnected) return;
    this.#visibilityObserver = new IntersectionObserver(
      (entries, obs) => {
        if (entries[0].isIntersecting) {
          obs.disconnect();
          this.#visibilityObserver = null;
          callback();
        }
      },
      { threshold: 0 }
    );
    this.#visibilityObserver.observe(this);
  }

  #track(event, properties = {}) {
    try {
      if (window.posthog?.capture) {
        window.posthog.capture(event, properties);
      }
    } catch {
      // PostHog not loaded
    }
  }
}

// ── Cart sync manager (module-scope, survives DOM morphing) ───────────────
//
// Section Rendering API replaces cart drawer HTML after every ATC/qty change,
// destroying the <cart-persistence> element and its debounce timer. By keeping
// the sync listener at module scope, it survives the morph cycle.

const cartSyncManager = {
  enabled: false,
  debounceTimer: null,
  syncing: false,
  pendingSync: false,
  restoring: false,

  handleCartUpdate() {
    if (!cartSyncManager.enabled) return;
    if (cartSyncManager.restoring) return;
    const email = localStorage.getItem(PERSIST_EMAIL_KEY);
    // Check logged-in from any live cart-persistence element
    const el = document.querySelector('cart-persistence');
    const isLoggedIn = el?.dataset.loggedIn === 'true';
    if (!email && !isLoggedIn) return;

    if (cartSyncManager.debounceTimer) clearTimeout(cartSyncManager.debounceTimer);
    cartSyncManager.debounceTimer = setTimeout(() => cartSyncManager.sync(email), DEBOUNCE_MS);
  },

  async sync(email) {
    if (cartSyncManager.syncing) {
      cartSyncManager.pendingSync = true;
      return;
    }
    cartSyncManager.syncing = true;

    try {
      const resp = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!resp.ok) throw new Error(`/cart.js ${resp.status}`);
      const cart = await resp.json();
      if (!cart.items?.length) return; // nothing to sync

      const items = cart.items.map((item) => ({
        variantId: String(item.variant_id || item.id),
        quantity: item.quantity,
        properties: item.properties || {},
        title: item.title || '',
        price: item.price ?? null,
        image: item.image || item.featured_image?.url || '',
        url: item.url || '',
        productType: item.product_type || '',
      }));

      const body = { items, updatedAt: new Date().toISOString() };
      if (email) body.email = email;

      const patchResp = await fetch(API_BASE, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      });
      if (!patchResp.ok) {
        console.warn('[cart-persist] Sync PATCH failed:', patchResp.status);
      }
    } catch (err) {
      console.error('[cart-persist] Sync error:', err);
    } finally {
      cartSyncManager.syncing = false;
      if (cartSyncManager.pendingSync) {
        cartSyncManager.pendingSync = false;
        cartSyncManager.sync(localStorage.getItem(PERSIST_EMAIL_KEY));
      }
    }
  },
};

document.addEventListener('cart:update', cartSyncManager.handleCartUpdate);

// ── Silent email capture (module-scope, survives DOM morphing) ─────────────
//
// When an email is captured via any non-save-prompt source (Klaviyo popup,
// newsletter signup, Shopify login), silently POST the current cart to cart-api
// so persistence activates without the user ever seeing the save prompt.

/**
 * Silently POST the current cart to cart-api for a captured email.
 * Returns true on success, false on any failure.
 */
async function silentCapture(email, source) {
  if (!email) return false;
  email = email.trim().toLowerCase();

  // Don't re-save if already persisting for this email
  if (localStorage.getItem(PERSIST_EMAIL_KEY) === email) return true;

  try {
    const resp = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!resp.ok) return false;
    const cart = await resp.json();
    if (!cart.items?.length) {
      // Cart is empty — store the email so future cart mutations sync,
      // but skip the POST (nothing to send yet).
      localStorage.setItem(PERSIST_EMAIL_KEY, email);
      cartSyncManager.enabled = true;
      console.log('[cart-persist] Silent capture (empty cart, deferred):', source);
      return true;
    }

    const items = cart.items.map((item) => ({
      variantId: String(item.variant_id || item.id),
      quantity: item.quantity,
      properties: item.properties || {},
      title: item.title || '',
      price: item.price ?? null,
      image: item.image || item.featured_image?.url || '',
      url: item.url || '',
      productType: item.product_type || '',
    }));

    let deviceId = localStorage.getItem(DEVICE_ID_KEY);
    if (!deviceId) {
      deviceId = crypto.randomUUID();
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    }

    const saveResp = await fetch(API_BASE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ email, items, source, deviceId }),
    });

    if (saveResp.ok) {
      localStorage.setItem(PERSIST_EMAIL_KEY, email);
      cartSyncManager.enabled = true;
      console.log('[cart-persist] Silent capture:', source);

      // Update any visible cart-persistence element to show the saved indicator
      const el = document.querySelector('cart-persistence');
      if (el && typeof el.updateIndicator === 'function') {
        el.updateIndicator(email);
      }
      return true;
    }
    return false;
  } catch (err) {
    console.error('[cart-persist] Silent capture error:', err);
    return false;
  }
}

// Klaviyo popup/form signup — fires client-side without page reload
document.addEventListener('klaviyo:signup-completed', (e) => {
  const email = e.detail?.email;
  if (email) silentCapture(email, 'klaviyo');
});

// Attentive popup/form signup — detected via webhook + iframe observer
document.addEventListener('attentive:signup-completed', (e) => {
  const email = e.detail?.email;
  if (email) silentCapture(email, 'attentive');
});

// Generic event for any future email capture source
document.addEventListener('cart-persist:email-captured', (e) => {
  const email = e.detail?.email;
  const source = e.detail?.source || 'unknown';
  if (email) silentCapture(email, source);
});

// Newsletter / email-signup forms — capture email on submit before page reload.
// These use {%- form 'customer' -%} which reloads the page on success.
document.addEventListener('submit', (e) => {
  const form = e.target.closest?.('.email-signup__form');
  if (!form) return;
  const input = form.querySelector('input[type="email"]');
  if (input?.value) {
    // Save to pending key — silentCapture's guard checks PERSIST_EMAIL_KEY,
    // so using a separate key avoids the collision. silentCapture promotes
    // to PERSIST_EMAIL_KEY on success; we clean up the pending key after.
    const email = input.value.trim().toLowerCase();
    localStorage.setItem(PERSIST_EMAIL_PENDING_KEY, email);
  }
});

// ── Toast listener (moved from inline script for CSP compliance) ──────────

document.addEventListener('cart:restored', (e) => {
  const toast = document.getElementById('CartRestoreToast');
  if (!toast) return;
  const msg = toast.querySelector('[data-restore-message]');
  const count = e.detail?.count || 0;
  const unavailable = e.detail?.unavailable || 0;
  if (count <= 0 && unavailable <= 0) return;

  let text;
  if (count > 0) {
    text = `We restored ${count} item${count !== 1 ? 's' : ''} from your saved cart.`;
    if (unavailable > 0) {
      text += ` ${unavailable} no longer available.`;
    }
  } else if (unavailable > 0) {
    text = `${unavailable} saved item${unavailable !== 1 ? 's are' : ' is'} no longer available.`;
  } else {
    return;
  }
  msg.textContent = text;
  toast.hidden = false;
  requestAnimationFrame(() => toast.classList.add('cart-restore-toast--visible'));
  setTimeout(() => {
    toast.classList.remove('cart-restore-toast--visible');
    setTimeout(() => { toast.hidden = true; }, 300);
  }, 4000);
});

if (!customElements.get('cart-persistence')) {
  customElements.define('cart-persistence', CartPersistence);
}
